<template>
  <div>
    <div :class="`sidebar ${collapsed && 'sidebar--collapsed'}`">
      <div class="sidebar__top">
        <router-link class="sidebar__logo" :to="`/home`">
          <img src="@/assets/imgs/logo.svg" />
        </router-link>
      </div>
      <div :key="route.name" v-for="route in filteredRoutes">
        <div
          v-if="
            !route.noLicenceDisabled ||
              (route.noLicenceDisabled &&
                userState.user.company.licence_analysis)
          "
          :class="
            `sidebar__links ${collapsed &&
              'sidebar__links--collapsed'} ${route.divider &&
              'sidebar__links--break'}`
          "
        >
          <router-link
            :class="
              `sidebar__link ${collapsed &&
                'sidebar__link--collapsed'} ${route.demoDisabled &&
                userState.user.role === 'demo' &&
                'sidebar__link--disabled'}`
            "
            active-class="sidebar__link--active"
            :key="route.name"
            :to="`/${route.path}`"
          >
            <bg-tooltip :disabled="!collapsed" placement="right">
              <template slot="content">{{ route.name }}</template>
              <template slot="target">
                <bg-icon
                  :class="
                    `sidebar__link-icon ${route.path === 'tests' &&
                      collapsed &&
                      'sidebar__link--active'} ${collapsed &&
                      'sidebar__link-icon--collapsed'}`
                  "
                  :name="route.icon"
                />
              </template>
            </bg-tooltip>
            <Transition name="slide-fade">
              <span class="sidebar__link-text" v-if="!collapsed">
                <div class="label" v-if="route.label">
                  <span>{{ route.label }}</span>
                </div>
                {{ route.name }}
              </span>
            </Transition>
          </router-link>
        </div>
      </div>
      <div
        :class="`sidebar__links ${collapsed && 'sidebar__links--collapsed'}`"
      >
        <a
          :class="`sidebar__link ${collapsed && 'sidebar__link--collapsed'}`"
          :href="`https://braingineers.crunch.help/`"
          target="_blank"
        >
          <bg-tooltip :disabled="!collapsed" placement="right">
            <template slot="content"
              >Support</template
            >
            <template slot="target">
              <bg-icon
                :class="
                  `sidebar__link-icon ${collapsed &&
                    'sidebar__link-icon--collapsed'}`
                "
                name="support"
              />
            </template>
          </bg-tooltip>
          <Transition name="slide-fade">
            <span class="sidebar__link-text" v-if="!collapsed">Support</span>
          </Transition>
        </a>
      </div>
      <div class="sidebar__button-container">
        <div
          @click="$router.push('/create-test')"
          :class="
            `sidebar__button ${collapsed && 'sidebar__button--collapsed'}`
          "
        >
          <bg-icon class="sidebar__button__icon" name="add" />
          <transition name="quickfadein">
            <span v-if="!collapsed">Create new test</span>
          </transition>
        </div>
      </div>
    </div>
    <div
      :class="`sidebar__drawer ${!collapsed && 'sidebar__drawer--collapsed'}`"
    >
      <div
        v-if="!isAdmin"
        :class="
          `sidebar__drawer__top ${isTestsOpen &&
            'sidebar__drawer__link--active-dropdown'}`
        "
        @click="toggleTestsSelector"
      >
        <div class="sidebar__drawer__top-text">
          {{ testState.name }}
        </div>
        <bg-icon
          :class="
            `sidebar__drawer__dropdown-icon ${isTestsOpen &&
              'sidebar__drawer__dropdown-icon--open'}`
          "
          name="chevronRight"
        ></bg-icon>
      </div>
      <Transition name="fallout">
        <bg-sidebar-dropdown
          v-click-outside="() => (isTestsOpen = false)"
          :hideClick="() => (isTestsOpen = false)"
          v-if="isTestsOpen"
          :top="true"
          :searchEnabled="true"
          :items="
            testsState.tests
              .filter((t) => {
                if (!isResearch) {
                  return t.state === 'done';
                } else {
                  return true;
                }
              })
              .map((p) => {
                let path = '';

                if (!isResearch) {
                  if (p.platform === 'video') {
                    path = `/video-test/${p.id}/summary`;
                  } else {
                    path = `/test/${p.id}/overview`;
                  }
                } else {
                  path = `/research/test/${p.id}/test-setup`;
                }

                return {
                  id: p.id,
                  label: `${p.name}`,
                  path: path,
                };
              })
          "
        />
      </Transition>
      <bg-optional-link
        :key="route.name"
        :class="
          `sidebar__drawer__links ${participants &&
            route.dropdown &&
            !isVideoOpen &&
            'sidebar__drawer__link--active'} ${isVideoOpen &&
            route.dropdown &&
            'sidebar__drawer__link--active-dropdown'} ${route.divider &&
            'sidebar__links--break'}`
        "
        v-for="route in testRoutes"
        :active-class="
          `sidebar__drawer__link--${
            route.dropdown ? 'active-dropdown' : 'active'
          }`
        "
        :to="getLinkPath(route.path)"
        :disabled="route.dropdown"
        @click.native="route.dropdown ? toggleVideoSelector() : null"
      >
        <Transition name="slide-fade">
          <span class="sidebar__drawer__link-text">{{ route.name }}</span>
        </Transition>

        <bg-icon
          :class="
            `sidebar__drawer__dropdown-icon ${isVideoOpen &&
              route.dropdown &&
              'sidebar__drawer__dropdown-icon--open'}`
          "
          v-if="route.dropdown"
          name="chevronRight"
        ></bg-icon>
        <Transition name="fallout">
          <bg-sidebar-dropdown
            v-click-outside="() => (isVideoOpen = false)"
            v-if="isVideoOpen && route.dropdown"
            :searchEnabled="false"
            :items="
              testState.participants.map((p) => {
                return {
                  id: p.id,
                  label: `Participant ${p.id}`,
                  path: `/test/${testState.id}/participant/${p.id}/recording`,
                };
              })
            "
          />
        </Transition>
      </bg-optional-link>
      <div
        class="researchButton"
        v-if="
          (this.userState.user.company.licence_research &&
            !isResearch &&
            this.$route.path.includes('/test/')) ||
            this.$route.path.includes('/video-test/')
        "
      >
        <bg-button
          text="Open in Research"
          style="width: 170px; margin:"
          @click="openInResearch"
        ></bg-button>
      </div>
      <div
        class="researchButton"
        v-if="this.userState.user.company.licence_research && isResearch"
      >
        <bg-button
          :text="reportButton"
          style="width: 170px; margin:"
          @click="viewReport"
        ></bg-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import "./styles.css";
</style>

<script>
import BgOptionalLink from "@/components/bg-optional-link";
import BgTooltip from "@/components/bg-tooltip";
import BgIcon from "@/components/bg-icon";
import BgSidebarDropdown from "./bg-sidebar-dropdown";
import { mapState } from "vuex";
import BgButton from "@/components/bg-button";

export default {
  name: "BgSidebar",
  components: {
    BgOptionalLink,
    BgIcon,
    BgSidebarDropdown,
    BgTooltip,
    BgButton,
  },
  props: {
    routes: Array,
    testRoutes: Array,
    location: String,
    test: Boolean,
    adRoutes: Array,
  },
  data() {
    return {
      isTestsOpen: false,
      isVideoOpen: false,
      isResearch: location.pathname.includes("/research/test"),
      isAdmin: location.pathname.includes("/admin/"),
      isPredictions: location.pathname.includes("/predictions/"),
      participants: location.pathname.includes("/participant/"),
      collapsed:
        location.pathname.includes("/test/") ||
        location.pathname.includes("/video-test/") ||
        location.pathname.includes("/admin/") ||
        location.pathname.includes("/research/"),
    };
  },
  methods: {
    toggleVideoSelector() {
      this.isVideoOpen = !this.isVideoOpen;
    },
    toggleTestsSelector() {
      this.isTestsOpen = !this.isTestsOpen;
    },
    getLinkPath(path) {
      let base = "test";
      if (location.pathname.includes("/research/test/")) {
        return `/research/test/${this.$route.params.id}/${path}`;
      } else if (location.pathname.includes("/admin/")) {
        return path;
      } else if (this.testState.platform === "video") {
        base = "video-test";
      } else if (location.pathname.includes("/predictions/")) {
        return path;
      }
      return `/${base}/${this.testState.id}/${path}`;
    },
    openInResearch() {
      this.$router.push(`/research/test/${this.$route.params.id}/test-setup`);
    },
    viewReport() {
      if (this.testState.platform != "video") {
        this.$router.push(`/test/${this.$route.params.id}/overview`);
      } else {
        this.$router.push(`/video-test/${this.$route.params.id}/summary`);
      }
    },
  },
  watch: {
    $route(to, from) {
      let t = to.path.includes("/test/") || to.path.includes("/create-test/");
      if (
        to.params.id &&
        !this.isAdmin &&
        to.params.id !== from.params.id &&
        t
      ) {
        this.$store.dispatch("fetchTest", to.params.id);
      }
      this.collapsed =
        to.path.includes("/test/") ||
        to.path.includes("/video-test/") ||
        to.path.includes("/admin/");
      this.participants = to.path.includes("/participant/");
      this.isResearch = to.path.includes("/research/test/");
      this.isAdmin = to.path.includes("/admin");
      this.isPredictions = to.path.includes("/predictions/");
    },
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
      userState: (state) => state.user,
      testsState: (state) => state.tests,
    }),
    reportButton: function() {
      if (this.testState.state != "done") {
        return "Preview report";
      } else {
        return "View report";
      }
    },
    filteredRoutes() {
      return this.routes.filter((route) => {
        const isDemoCompany = this.userState.user.company && this.userState.user.company.name === "Demo";
        // Hide 'Members' for demo users
        if ((route.name === "Members" || route.name === "Organisation") && isDemoCompany) {
          return false;
        }
        return true;
      });
    },
  },
  created() {
    // TODO: UPDATE NAME
    const user = {
      email: this.userState.user.email,
      name:
        this.userState.user.profile.first_name +
        " " +
        this.userState.user.profile.last_name,
      user_id: this.userState.user.id,
      custom_data: {},
    };
    if (typeof window !== "undefined" && window.HelpCrunch) {
      window.HelpCrunch("updateUser", user);
      window.HelpCrunch("showChatWidget");
    }
  },
};
</script>
